import { useEffect, useRef, useState } from 'react';
import { BarChart3, Check, CircleSlash, Loader2, Search } from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@lognai/synapse-design-system-v2';
import {
  HighchartsRenderer,
  executeVisualBatch,
  listVisuals,
  resolveChartTheme,
  type BatchResult,
  type ChartThemePayload,
  type VisualDefinition,
} from '@lognai/plugin-analytics-ui';

const DOMAINS = ['orders', 'inventory', 'warehouse', 'shipments', 'procurement', 'products'];

/** The library exposes ~70 charts; fetch the whole set so none are hidden. */
const PAGE_SIZE = 100;

/** Charts executed per batch request as cards scroll into view. */
const PREVIEW_BATCH = 8;

const businessLabel = (value: string) =>
  value.replace(/[_-]/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());

/**
 * One library card. Reports itself visible so its preview is only executed when
 * it actually scrolls into view — executing all ~70 at once would hammer Cube.
 */
function PickerCard({
  visual,
  execution,
  added,
  theme,
  onVisible,
  onSelect,
}: {
  visual: VisualDefinition;
  execution?: BatchResult;
  added: boolean;
  theme?: ChartThemePayload;
  onVisible: () => void;
  onSelect: () => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onVisible();
          observer.disconnect();
        }
      },
      { rootMargin: '160px' }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [onVisible]);

  const rowState = execution?.result?.meta?.rowState;

  return (
    <button
      ref={ref}
      type="button"
      disabled={added}
      onClick={onSelect}
      className="group flex min-h-[210px] flex-col overflow-hidden rounded-lg border border-border text-left transition hover:border-primary/40 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60"
    >
      <div className="h-[112px] shrink-0 overflow-hidden border-b border-border bg-muted/10 p-1">
        {execution?.result && rowState === 'ready' ? (
          <HighchartsRenderer visual={execution.result.visual} height={104} compact theme={theme} />
        ) : rowState === 'zero' ? (
          <div className="grid h-full place-items-center text-center">
            <div>
              <div className="mx-auto grid h-7 w-7 place-items-center rounded-md bg-muted text-muted-foreground">
                <CircleSlash size={14} />
              </div>
              <p className="mt-1.5 text-[11px] font-medium">Zero for this period</p>
            </div>
          </div>
        ) : rowState === 'empty' ? (
          <div className="grid h-full place-items-center px-4 text-center text-[11px] text-muted-foreground">
            No data for the current company
          </div>
        ) : execution && !execution.ok ? (
          <div className="grid h-full place-items-center px-4 text-center text-[11px] text-muted-foreground">
            This chart could not be loaded
          </div>
        ) : (
          <div className="grid h-full place-items-center">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col p-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="line-clamp-2 text-[13px] font-semibold leading-4 text-foreground">
            {visual.title}
          </h3>
          {added && (
            <Badge variant="secondary" className="shrink-0">
              <Check size={11} /> Added
            </Badge>
          )}
        </div>
        <p className="mt-1 line-clamp-2 text-[11px] leading-4 text-muted-foreground">
          {visual.businessQuestion}
        </p>
        <div className="mt-auto flex flex-wrap gap-1 pt-2">
          {visual.domains.slice(0, 2).map((item: string) => (
            <Badge key={item} variant="outline" className="text-[10px]">
              {businessLabel(item)}
            </Badge>
          ))}
        </div>
      </div>
    </button>
  );
}

/**
 * Picks a saved analytics chart to pin onto the workspace.
 *
 * Mirrors the Analytics chart library: the same governed list, the same live
 * previews, so what a user picks here is what they saw there.
 */
export function ChartCardPicker({
  open,
  onOpenChange,
  onSelect,
  alreadyAdded,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (visual: VisualDefinition) => void;
  alreadyAdded: Set<string>;
}) {
  const [query, setQuery] = useState('');
  const [domain, setDomain] = useState('all');
  const [items, setItems] = useState<VisualDefinition[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [theme, setTheme] = useState<ChartThemePayload>();
  const [visibleIds, setVisibleIds] = useState<Set<string>>(new Set());
  const [previews, setPreviews] = useState<Record<string, BatchResult>>({});
  const inFlight = useRef(new Set<string>());

  useEffect(() => {
    if (!open) return;
    resolveChartTheme()
      .then((resolved) => setTheme(resolved.payload))
      .catch(() => undefined);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let live = true;
    setBusy(true);
    setError(undefined);
    const timer = window.setTimeout(
      () => {
        listVisuals({
          q: query || undefined,
          domains: domain === 'all' ? undefined : [domain],
          sort: 'relevance',
          pageSize: PAGE_SIZE,
        })
          .then((page) => {
            if (!live) return;
            setItems(page.items);
            // Previews are keyed by visual id, so they stay valid across
            // filter changes; only the visibility set needs resetting.
            setVisibleIds(new Set());
          })
          .catch(() => {
            if (live) setError('The chart library could not be loaded.');
          })
          .finally(() => {
            if (live) setBusy(false);
          });
      },
      query ? 250 : 0
    );
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [domain, open, query]);

  // Execute previews for whatever has scrolled into view, a batch at a time.
  useEffect(() => {
    const ids = [...visibleIds]
      .filter((id) => !previews[id] && !inFlight.current.has(id))
      .slice(0, PREVIEW_BATCH);
    if (!ids.length) return;
    ids.forEach((id) => inFlight.current.add(id));
    executeVisualBatch(ids.map((visualId) => ({ visualId })))
      .then((batch) => {
        setPreviews((current) => ({
          ...current,
          ...Object.fromEntries(batch.results.map((result) => [result.visualId, result])),
        }));
      })
      .catch(() => undefined)
      .finally(() => ids.forEach((id) => inFlight.current.delete(id)));
  }, [previews, visibleIds]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="flex max-h-[88vh] w-[min(1180px,95vw)] max-w-none flex-col">
        <Dialog.Header>
          <Dialog.Title>Add a chart</Dialog.Title>
          <Dialog.Description>
            Pick a saved chart from the analytics library. Previews use the active company's data.
          </Dialog.Description>
        </Dialog.Header>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-2.5 text-muted-foreground"
              size={16}
            />
            <Input
              className="pl-9"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search charts"
              aria-label="Search charts"
            />
          </div>
          <Select value={domain} onValueChange={setDomain}>
            <SelectTrigger className="h-9 w-auto min-w-[170px]" aria-label="Filter by business area">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All business areas</SelectItem>
              {DOMAINS.map((item) => (
                <SelectItem key={item} value={item}>
                  {businessLabel(item)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">{items.length} charts</span>
        </div>

        {error && (
          <div className="rounded-lg border border-destructive-border bg-destructive-subtle px-4 py-3 text-sm text-destructive-foreground">
            {error}
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto">
          {busy && items.length === 0 ? (
            <div className="grid min-h-[280px] place-items-center">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <div className="grid min-h-[280px] place-items-center text-center">
              <div>
                <BarChart3 className="mx-auto text-muted-foreground" />
                <p className="mt-3 text-sm font-medium">No matching charts</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Try a broader search or a different business area.
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-3 pb-1">
              {items.map((visual) => (
                <PickerCard
                  key={visual.id}
                  visual={visual}
                  execution={previews[visual.id]}
                  added={alreadyAdded.has(visual.id)}
                  theme={theme}
                  onVisible={() =>
                    setVisibleIds((current) =>
                      current.has(visual.id) ? current : new Set(current).add(visual.id)
                    )
                  }
                  onSelect={() => onSelect(visual)}
                />
              ))}
            </div>
          )}
        </div>

        <Dialog.Footer>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
}
